import React from 'react';
import { ArrowRight } from 'lucide-react';
import { buildSubdomainUrl } from '../config/domain';

const PortalNotFound: React.FC = () => {
  const subdomain = window.location.hostname.split('.')[0];
  const mainUrl = buildSubdomainUrl('app');

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 flex items-center justify-center px-4">
      {/* Decorative blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '2s' }} />
        <div className="absolute top-1/3 left-1/2 w-80 h-80 bg-cyan-400 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse" style={{ animationDelay: '4s' }} />
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }} />
      </div>

      <div className="relative z-10 max-w-lg w-full text-center">
        {/* Illustration SVG */}
        <div className="mb-8 flex justify-center">
          <svg width="200" height="160" viewBox="0 0 200 160" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Building */}
            <rect x="60" y="40" width="80" height="100" rx="4" fill="url(#buildGrad)" opacity="0.9" />
            <rect x="72" y="55" width="16" height="16" rx="2" fill="#1e3a5f" opacity="0.6" />
            <rect x="112" y="55" width="16" height="16" rx="2" fill="#1e3a5f" opacity="0.6" />
            <rect x="72" y="80" width="16" height="16" rx="2" fill="#1e3a5f" opacity="0.6" />
            <rect x="112" y="80" width="16" height="16" rx="2" fill="#1e3a5f" opacity="0.6" />
            <rect x="90" y="105" width="20" height="35" rx="2" fill="#1e3a5f" opacity="0.8" />
            {/* Antenna */}
            <rect x="98" y="20" width="4" height="20" rx="2" fill="#60a5fa" />
            <circle cx="100" cy="16" r="5" fill="#60a5fa" opacity="0.8" />
            <circle cx="100" cy="16" r="10" fill="none" stroke="#60a5fa" strokeWidth="1" opacity="0.3" />
            <circle cx="100" cy="16" r="16" fill="none" stroke="#60a5fa" strokeWidth="1" opacity="0.15" />
            {/* Shield with X */}
            <g transform="translate(140, 90)">
              <path d="M20 4 L36 12 V24 C36 34 28 42 20 46 C12 42 4 34 4 24 V12 L20 4Z" fill="url(#shieldGrad)" opacity="0.95" />
              <line x1="14" y1="20" x2="26" y2="32" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
              <line x1="26" y1="20" x2="14" y2="32" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
            </g>
            {/* Floating particles */}
            <circle cx="30" cy="60" r="2" fill="#60a5fa" opacity="0.5">
              <animate attributeName="cy" values="60;50;60" dur="3s" repeatCount="indefinite" />
            </circle>
            <circle cx="170" cy="50" r="1.5" fill="#a78bfa" opacity="0.5">
              <animate attributeName="cy" values="50;40;50" dur="4s" repeatCount="indefinite" />
            </circle>
            <circle cx="45" cy="120" r="1.5" fill="#22d3ee" opacity="0.4">
              <animate attributeName="cy" values="120;110;120" dur="3.5s" repeatCount="indefinite" />
            </circle>
            <circle cx="160" cy="130" r="2" fill="#60a5fa" opacity="0.3">
              <animate attributeName="cy" values="130;120;130" dur="2.5s" repeatCount="indefinite" />
            </circle>
            <defs>
              <linearGradient id="buildGrad" x1="60" y1="40" x2="140" y2="140" gradientUnits="userSpaceOnUse">
                <stop stopColor="#3b82f6" />
                <stop offset="1" stopColor="#6366f1" />
              </linearGradient>
              <linearGradient id="shieldGrad" x1="4" y1="4" x2="36" y2="46" gradientUnits="userSpaceOnUse">
                <stop stopColor="#ef4444" />
                <stop offset="1" stopColor="#dc2626" />
              </linearGradient>
            </defs>
          </svg>
        </div>

        {/* Content */}
        <h1 className="text-3xl font-bold text-white mb-3">
          Portal no disponible
        </h1>

        <p className="text-blue-200/80 text-lg mb-1">
          El portal <span className="font-semibold text-blue-300">{subdomain}.kessan.cl</span> no existe o no está habilitado.
        </p>

        <p className="text-blue-300/40 text-sm mb-10">
          Si crees que esto es un error, contacta al administrador de tu empresa.
        </p>

        <a
          href={mainUrl}
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-medium rounded-xl hover:from-blue-600 hover:to-indigo-600 transition-all shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40"
        >
          Ir a app.kessan.cl
          <ArrowRight className="w-4 h-4" />
        </a>

        {/* Subtle branding */}
        <div className="mt-16 flex items-center justify-center gap-2 opacity-30">
          <div className="w-6 h-6 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-md flex items-center justify-center">
            <span className="text-white font-bold text-xs">K</span>
          </div>
          <span className="text-blue-300 text-xs font-medium">Kessan</span>
        </div>
      </div>
    </div>
  );
};

export default PortalNotFound;
